import { useEffect, useMemo, useState } from "react";
import LayoutSubmaster from "../../components/layoutSubmasterConfig/layoutSubmaster";
import LocalStorage from "../../helpers/localstorage";
import { apiClientSubmaster } from "../../services/setupClientSubmaster/apiClientSubmaster";
import { toast } from "react-toastify";

type ReportType =
  | "epi-report"
  | "epi-output-report"
  | "employee-report"
  | "top-largest"
  | "top-employee-withdrawals"
  | "top-smallest"
  | "latest-by-employee"
  | "low-stock"
  | "no-withdrawal-by-period"
  | "withdrawals-by-employee"
  | "cost-by-employee"
  | "epi-output-cost-report"
  | "epi-output-employee-cost-report"
  | "epi-output-grouped-employee-cost-report";

type ReportCard = {
  type: ReportType;
  title: string;
  description: string;
  icon: string;
};

type ReportPeriod = {
  startDate: string;
  endDate: string;
};

type ReportFormat = "pdf" | "excel";

type ReportCompany = {
  value: string;
  name: string;
  cnpj: string;
  isMain: boolean;
};

type ReportEmployee = {
  id: number;
  name: string;
  companyId: number | null;
  companyCnpj: string;
  department: string;
};

const baseReportCards: ReportCard[] = [
  {
    type: "epi-report",
    title: "Relatório de EPIs",
    description:
      "Gera um relatório com os dados cadastrais e a posição atual de estoque dos EPIs.",
    icon: "bi-shield-check",
  },
  {
    type: "top-largest",
    title: "Top 10 maiores retiradas de EPI",
    description: "Gera um relatório com os EPIs de maior volume total de retirada.",
    icon: "bi-graph-up-arrow",
  },
  {
    type: "top-smallest",
    title: "Top 10 menores retiradas de EPI",
    description: "Gera um relatório com os EPIs de menor volume total de retirada.",
    icon: "bi-graph-down-arrow",
  },
  {
    type: "latest-by-employee",
    title: "Última retirada por funcionário",
    description: "Gera um relatório com a data da última retirada de EPI por funcionário.",
    icon: "bi-file-earmark-person",
  },
];

const defaultReportError = "Erro ao gerar o relatório.";

const lowStockReportCard: ReportCard = {
  type: "low-stock",
  title: "EPIs com estoque baixo",
  description:
    "Gera um relatório listando os EPIs cuja quantidade atual está igual ou abaixo da quantidade mínima.",
  icon: "bi-box-seam",
};

const epiRetiradaReportCard: ReportCard = {
  type: "no-withdrawal-by-period",
  title: "Relatório de Funcionários Sem Retirada de EPI",
  description:
    "Gera um relatório com os funcionários que não retiraram nenhum EPI no período selecionado.",
  icon: "bi-person-exclamation",
};

const epiOutputReportCard: ReportCard = {
  type: "epi-output-report",
  title: "Relatório de Saída de EPIs",
  description: "Soma de todas as retiradas de EPIs por período.",
  icon: "bi-box-arrow-up-right",
};

const epiOutputCostReportCard: ReportCard = {
  type: "epi-output-cost-report",
  title: "Custo de Saída de EPI por Empresa",
  description: "Custo de Saída de EPI por Empresa",
  icon: "bi-cash-stack",
};

const epiOutputEmployeeCostReportCard: ReportCard = {
  type: "epi-output-employee-cost-report",
  title: "Custo de Saída de EPI por Empresa e Funcionário",
  description: "Custo de Saída de EPI por Empresa e Funcionário",
  icon: "bi-people-fill",
};

const epiOutputGroupedEmployeeCostReportCard: ReportCard = {
  type: "epi-output-grouped-employee-cost-report",
  title: "Custo de Saída de EPI Agrupado por Funcionário",
  description: "Custo de Saída de EPI Agrupado por Funcionário",
  icon: "bi-collection-fill",
};

const topEmployeeWithdrawalsReportCard: ReportCard = {
  type: "top-employee-withdrawals",
  title: "Top 10 funcionários com maiores retiradas de EPI",
  description: "Top 10 funcionários que mais retiraram EPI.",
  icon: "bi-people-fill",
};

const employeeWithdrawalReportCard: ReportCard = {
  type: "withdrawals-by-employee",
  title: "Relatório de Retirada de EPI por funcionário",
  description:
    "Gera um relatório com as retiradas de EPI do funcionário selecionado no período informado.",
  icon: "bi-person-lines-fill",
};

const employeeEpiCostReportCard: ReportCard = {
  type: "cost-by-employee",
  title: "Custo de EPI por funcionário",
  description:
    "Gera um relatório com as retiradas e o preço médio dos EPIs do funcionário selecionado.",
  icon: "bi-currency-dollar",
};

const employeesReportCard: ReportCard = {
  type: "employee-report",
  title: "Relatório de Funcionários",
  description:
    "Gera um relatório com nome, CPF, setor, cargo, data de admissão e status dos funcionários.",
  icon: "bi-person-vcard",
};

const periodLabelStyle = {
  fontSize: "0.78rem",
  fontWeight: 700,
  color: "#5d7893",
};

const toInputDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

const getFirstDayOfCurrentMonth = () => {
  const now = new Date();
  return toInputDate(new Date(now.getFullYear(), now.getMonth(), 1));
};

const getToday = () => toInputDate(new Date());

const formatCnpj = (value: string) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 14
    ? digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5")
    : value;
};

const normalizeCnpj = (value: string) => String(value || "").replace(/\D/g, "");

const isCatalogReport = (reportType: ReportType) =>
  reportType === "epi-report" ||
  reportType === "epi-output-report" ||
  reportType === "epi-output-cost-report" ||
  reportType === "epi-output-employee-cost-report" ||
  reportType === "epi-output-grouped-employee-cost-report" ||
  reportType === "employee-report";

const isEmployeeSpecificReport = (reportType: ReportType) =>
  reportType === "withdrawals-by-employee" || reportType === "cost-by-employee";

const reportTypes: ReportType[] = [
  "epi-report",
  "epi-output-report",
  "employee-report",
  "top-largest",
  "top-employee-withdrawals",
  "top-smallest",
  "latest-by-employee",
  "low-stock",
  "no-withdrawal-by-period",
  "withdrawals-by-employee",
  "cost-by-employee",
  "epi-output-cost-report",
  "epi-output-employee-cost-report",
  "epi-output-grouped-employee-cost-report",
];

const createInitialPeriods = (): Record<ReportType, ReportPeriod> =>
  reportTypes.reduce(
    (periods, reportType) => ({
      ...periods,
      [reportType]: {
        startDate: getFirstDayOfCurrentMonth(),
        endDate: getToday(),
      },
    }),
    {} as Record<ReportType, ReportPeriod>
  );

const createInitialCompanySelections = (): Record<ReportType, string> =>
  reportTypes.reduce(
    (selections, reportType) => ({
      ...selections,
      [reportType]: "",
    }),
    {} as Record<ReportType, string>
  );

function ConfigAdditionalReportPage() {
  const storage = useMemo(() => new LocalStorage(), []);
  const authToken = useMemo(() => String(storage.getAuthToken() || ""), [storage]);
  const submasterId = useMemo(() => Number(storage.getId() || 0), [storage]);
  const apiClient = useMemo(() => apiClientSubmaster(authToken), [authToken]);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [loadingReport, setLoadingReport] = useState<ReportType | null>(null);
  const [epiReportAllowed, setEpiReportAllowed] = useState(false);
  const [topLargestAllowed, setTopLargestAllowed] = useState(false);
  const [topSmallestAllowed, setTopSmallestAllowed] = useState(false);
  const [latestByEmployeeAllowed, setLatestByEmployeeAllowed] = useState(false);
  const [lowStockAllowed, setLowStockAllowed] = useState(false);
  const [epiOutputReportAllowed, setEpiOutputReportAllowed] = useState(false);
  const [epiOutputCostReportAllowed, setEpiOutputCostReportAllowed] = useState(false);
  const [epiOutputEmployeeCostReportAllowed, setEpiOutputEmployeeCostReportAllowed] = useState(false);
  const [epiOutputGroupedEmployeeCostReportAllowed, setEpiOutputGroupedEmployeeCostReportAllowed] = useState(false);
  const [epiRetiradaAllowed, setEpiRetiradaAllowed] = useState(false);
  const [employeeWithdrawalAllowed, setEmployeeWithdrawalAllowed] = useState(false);
  const [employeeEpiCostAllowed, setEmployeeEpiCostAllowed] = useState(false);
  const [topEmployeeWithdrawalsAllowed, setTopEmployeeWithdrawalsAllowed] = useState(false);
  const [employeesReportAllowed, setEmployeesReportAllowed] = useState(false);
  const [excelReportAllowed, setExcelReportAllowed] = useState(false);
  const [reportPeriods, setReportPeriods] = useState(createInitialPeriods);
  const [reportCompanies, setReportCompanies] = useState<ReportCompany[]>([]);
  const [reportCompanyIds, setReportCompanyIds] = useState(createInitialCompanySelections);
  const [reportCompaniesLoaded, setReportCompaniesLoaded] = useState(false);
  const [reportEmployees, setReportEmployees] = useState<ReportEmployee[]>([]);
  const [reportEmployeesLoaded, setReportEmployeesLoaded] = useState(false);
  const [reportEmployeeId, setReportEmployeeId] = useState("");
  const [costReportEmployeeId, setCostReportEmployeeId] = useState("");
  const [noWithdrawalDepartment, setNoWithdrawalDepartment] = useState("");

  useEffect(() => {
    let active = true;

    const loadPermissions = async () => {
      try {
        const response = await apiClient.post("/reportPermissions", {});
        if (active) {
          setEpiReportAllowed(response?.data?.report_epi_permission_enabled !== false);
          setTopLargestAllowed(
            response?.data?.report_top_largest_permission_enabled !== false
          );
          setTopSmallestAllowed(
            response?.data?.report_top_smallest_permission_enabled !== false
          );
          setLatestByEmployeeAllowed(
            response?.data?.report_latest_by_employee_permission_enabled !== false
          );
          setLowStockAllowed(response?.data?.report_low_stock_permission_enabled === true);
          setEpiOutputReportAllowed(
            response?.data?.report_epi_output_permission_enabled === true
          );
          setEpiOutputCostReportAllowed(
            response?.data?.report_epi_output_cost_permission_enabled === true
          );
          setEpiOutputEmployeeCostReportAllowed(
            response?.data?.report_epi_output_employee_cost_permission_enabled === true
          );
          setEpiOutputGroupedEmployeeCostReportAllowed(
            response?.data?.report_epi_output_grouped_employee_cost_permission_enabled === true
          );
          setEpiRetiradaAllowed(response?.data?.report_epi_retirada_permission_enabled === true);
          setEmployeeWithdrawalAllowed(
            response?.data?.report_employee_withdrawal_permission_enabled === true
          );
          setEmployeeEpiCostAllowed(
            response?.data?.report_employee_cost_permission_enabled === true
          );
          setTopEmployeeWithdrawalsAllowed(
            response?.data?.report_top_employee_withdrawal_permission_enabled === true
          );
          setEmployeesReportAllowed(
            response?.data?.report_employees_permission_enabled === true
          );
          setExcelReportAllowed(
            response?.data?.report_excel_permission_enabled === true
          );
        }
      } catch (error) {
        console.error("Erro ao carregar permissões de relatório:", error);
      }
    };

    const loadCompanies = async () => {
      try {
        const data = new FormData();
        data.append("submaster_id", String(submasterId));
        data.append("action", "list");

        const [linkedCompaniesResponse, mainCompanyResponse] = await Promise.all([
          apiClient.post(
            `/crud/companyForSubmaster/submaster_company/${submasterId}`,
            data,
            { headers: { "Content-Type": "multipart/form-data" } }
          ),
          apiClient.post(`/crud/getdata/Submaster/${submasterId}/true`),
        ]);
        const items = linkedCompaniesResponse?.data?.data;
        const mainCompanyData = Array.isArray(mainCompanyResponse?.data)
          ? mainCompanyResponse.data[0]
          : mainCompanyResponse?.data;

        if (!Array.isArray(items) || !mainCompanyData) {
          throw new Error("Resposta inválida ao carregar empresas.");
        }

        const linkedCompanies = items
          .map((item: any) => ({
            value: String(Number(item?.id || 0)),
            name: String(item?.name_company || "Empresa sem nome"),
            cnpj: String(item?.cnpj || ""),
            isMain: false,
          }))
          .filter((company: ReportCompany) => Number(company.value) > 0)
          .sort((first: ReportCompany, second: ReportCompany) =>
            first.name.localeCompare(second.name, "pt-BR", { sensitivity: "base" })
          );
        const mainCompany: ReportCompany = {
          value: "main",
          name: String(
            mainCompanyData?.fantasy_name ||
              mainCompanyData?.name ||
              "Empresa principal"
          ),
          cnpj: String(mainCompanyData?.cnpj || ""),
          isMain: true,
        };
        const seenCnpjs = new Set<string>();
        const mainCnpj = normalizeCnpj(mainCompany.cnpj);
        if (mainCnpj) {
          seenCnpjs.add(mainCnpj);
        }
        const uniqueLinkedCompanies = linkedCompanies.filter((company) => {
          const companyCnpj = normalizeCnpj(company.cnpj);
          if (!companyCnpj || !seenCnpjs.has(companyCnpj)) {
            if (companyCnpj) {
              seenCnpjs.add(companyCnpj);
            }
            return true;
          }
          return false;
        });
        const companies = [mainCompany, ...uniqueLinkedCompanies];

        if (active) {
          setReportCompanies(companies);
          setReportCompaniesLoaded(true);
          setReportCompanyIds((currentSelections) => {
            const nextSelections = {
              ...currentSelections,
              "epi-report": "all",
              "epi-output-report": "all",
              "epi-output-cost-report": "all",
              "epi-output-employee-cost-report": "all",
              "epi-output-grouped-employee-cost-report": "all",
              "employee-report": "all",
            };

            if (companies.length === 1) {
              const onlyCompanyId = companies[0].value;
              reportTypes.forEach((reportType) => {
                nextSelections[reportType] = isCatalogReport(reportType) ? "all" : onlyCompanyId;
              });
            }

            return nextSelections;
          });
        }
      } catch (error) {
        console.error("Erro ao carregar empresas para os relatórios:", error);
        if (active) {
          setReportCompaniesLoaded(false);
          toast.error("Não foi possível carregar as empresas dos relatórios.");
        }
      }
    };

    const loadEmployees = async () => {
      try {
        const response = await apiClient.post(
          `/crud/getdata/EmployeesSubmaster/${submasterId}/false`
        );
        const items = response?.data;

        if (!Array.isArray(items)) {
          throw new Error("Resposta inválida ao carregar funcionários.");
        }

        const employees = items
          .filter((item: any) => item?.activeemployee !== false)
          .map((item: any) => {
            const rawCompanyId =
              item?.submasterCompanyId ??
              item?.submaster_company_id ??
              item?.companyId ??
              item?.company_id ??
              null;

            return {
              id: Number(item?.id || 0),
              name: String(item?.name || "Funcionário sem nome"),
              companyId:
                rawCompanyId === null || rawCompanyId === undefined
                  ? null
                  : Number(rawCompanyId),
              companyCnpj: String(
                item?.cnpj_company ||
                  item?.company?.cnpj ||
                  item?.submaster_company?.cnpj ||
                  ""
              ),
              department: String(item?.department || "").trim(),
            };
          })
          .filter((employee: ReportEmployee) => employee.id > 0)
          .sort((first: ReportEmployee, second: ReportEmployee) =>
            first.name.localeCompare(second.name, "pt-BR", { sensitivity: "base" })
          );

        if (active) {
          setReportEmployees(employees);
          setReportEmployeesLoaded(true);
        }
      } catch (error) {
        console.error("Erro ao carregar funcionários para os relatórios:", error);
        if (active) {
          setReportEmployeesLoaded(false);
          toast.error("Não foi possível carregar os funcionários dos relatórios.");
        }
      }
    };

    loadPermissions();
    loadCompanies();
    loadEmployees();

    return () => {
      active = false;
    };
  }, [apiClient, submasterId]);

  const reportCards = useMemo<ReportCard[]>(() => {
    const cards: ReportCard[] = [];

    if (epiReportAllowed) {
      cards.push(baseReportCards[0]);
    }

    if (topLargestAllowed) {
      cards.push(baseReportCards[1]);
    }

    if (topSmallestAllowed) {
      cards.push(baseReportCards[2]);
    }

    if (latestByEmployeeAllowed) {
      cards.push(baseReportCards[3]);
    }

    if (lowStockAllowed) {
      cards.push(lowStockReportCard);
    }

    if (epiOutputReportAllowed) {
      cards.push(epiOutputReportCard);
    }

    if (epiOutputCostReportAllowed) {
      cards.push(epiOutputCostReportCard);
    }

    if (epiOutputEmployeeCostReportAllowed) {
      cards.push(epiOutputEmployeeCostReportCard);
    }

    if (epiOutputGroupedEmployeeCostReportAllowed) {
      cards.push(epiOutputGroupedEmployeeCostReportCard);
    }

    if (epiRetiradaAllowed) {
      cards.push(epiRetiradaReportCard);
    }

    if (employeeWithdrawalAllowed) {
      cards.push(employeeWithdrawalReportCard);
    }

    if (employeeEpiCostAllowed) {
      cards.push(employeeEpiCostReportCard);
    }

    if (topEmployeeWithdrawalsAllowed) {
      cards.push(topEmployeeWithdrawalsReportCard);
    }

    if (employeesReportAllowed) {
      cards.push(employeesReportCard);
    }

    return cards;
  }, [
    epiReportAllowed,
    topLargestAllowed,
    topSmallestAllowed,
    latestByEmployeeAllowed,
    lowStockAllowed,
    epiOutputReportAllowed,
    epiOutputCostReportAllowed,
    epiOutputEmployeeCostReportAllowed,
    epiOutputGroupedEmployeeCostReportAllowed,
    epiRetiradaAllowed,
    employeeWithdrawalAllowed,
    employeeEpiCostAllowed,
    topEmployeeWithdrawalsAllowed,
    employeesReportAllowed,
  ]);

  const reportEmployeesByCompany = useMemo<Record<string, ReportEmployee[]>>(() => {
    const employeesByCompany = reportCompanies.reduce<Record<string, ReportEmployee[]>>((result, company) => {
      const selectedCnpj = normalizeCnpj(company.cnpj);
      result[company.value] = reportEmployees.filter((employee) => {
        const sameCnpj =
          selectedCnpj !== "" && normalizeCnpj(employee.companyCnpj) === selectedCnpj;

        if (company.isMain) {
          return employee.companyId === null || sameCnpj;
        }

        return employee.companyId === Number(company.value) || sameCnpj;
      });
      return result;
    }, {});
    employeesByCompany.all = reportEmployees;
    return employeesByCompany;
  }, [reportCompanies, reportEmployees]);

  const employeeReportEmployees =
    reportEmployeesByCompany[reportCompanyIds["withdrawals-by-employee"]] || [];
  const employeeCostReportEmployees =
    reportEmployeesByCompany[reportCompanyIds["cost-by-employee"]] || [];
  const employeesByEmployeeReport: Partial<Record<ReportType, ReportEmployee[]>> = {
    "withdrawals-by-employee": employeeReportEmployees,
    "cost-by-employee": employeeCostReportEmployees,
  };

  const getSelectedEmployeeId = (reportType: ReportType) =>
    reportType === "cost-by-employee" ? costReportEmployeeId : reportEmployeeId;

  const updateSelectedEmployeeId = (reportType: ReportType, employeeId: string) => {
    if (reportType === "cost-by-employee") {
      setCostReportEmployeeId(employeeId);
    } else {
      setReportEmployeeId(employeeId);
    }
  };

  const noWithdrawalDepartments = useMemo(() => {
    const employees =
      reportEmployeesByCompany[reportCompanyIds["no-withdrawal-by-period"]] || [];
    const departments = new Map<string, string>();

    employees.forEach((employee) => {
      const department = employee.department.trim();
      if (department) {
        const normalizedDepartment = department.toLocaleLowerCase("pt-BR");
        if (!departments.has(normalizedDepartment)) {
          departments.set(normalizedDepartment, department);
        }
      }
    });

    return Array.from(departments.values()).sort((first, second) =>
      first.localeCompare(second, "pt-BR", { sensitivity: "base" })
    );
  }, [reportCompanyIds, reportEmployeesByCompany]);

  const extractErrorMessage = async (data: Blob): Promise<string> => {
    try {
      const parsed = JSON.parse(await data.text());
      return String(parsed?.error || parsed?.message || "").trim() || defaultReportError;
    } catch (error) {
      return defaultReportError;
    }
  };

  const updateReportPeriod = (
    reportType: ReportType,
    field: keyof ReportPeriod,
    value: string
  ) => {
    setReportPeriods((currentPeriods) => ({
      ...currentPeriods,
      [reportType]: {
        ...currentPeriods[reportType],
        [field]: value,
      },
    }));
  };

  const updateReportCompany = (reportType: ReportType, companyId: string) => {
    setReportCompanyIds((currentSelections) => ({
      ...currentSelections,
      [reportType]: companyId,
    }));

    if (reportType === "withdrawals-by-employee") {
      setReportEmployeeId("");
    }
    if (reportType === "cost-by-employee") {
      setCostReportEmployeeId("");
    }
    if (reportType === "no-withdrawal-by-period") {
      setNoWithdrawalDepartment("");
    }
  };

  const fetchReportFile = async (
    reportType: ReportType,
    format: ReportFormat
  ): Promise<Blob> => {
    const { startDate, endDate } = reportPeriods[reportType];

    if (!startDate || !endDate) {
      throw new Error("Informe a data início e a data fim.");
    }
    if (endDate < startDate) {
      throw new Error("A data de fim não pode ser anterior à data de início.");
    }
    if (!reportCompaniesLoaded) {
      throw new Error("Aguarde o carregamento das empresas.");
    }

    const companyId = reportCompanyIds[reportType];
    if (reportCompanies.length > 1 && !companyId) {
      throw new Error("Selecione a empresa do relatório.");
    }
    if (isEmployeeSpecificReport(reportType)) {
      if (!reportEmployeesLoaded) {
        throw new Error("Aguarde o carregamento dos funcionários.");
      }
      const selectedEmployeeId = getSelectedEmployeeId(reportType);
      const availableEmployees = employeesByEmployeeReport[reportType] || [];
      if (!selectedEmployeeId) {
        throw new Error("Selecione o funcionário do relatório.");
      }
      if (!availableEmployees.some((employee) => String(employee.id) === selectedEmployeeId)) {
        throw new Error("O funcionário selecionado não pertence à empresa informada.");
      }
    }

    const payload = {
      reportType,
      startDate,
      endDate,
      companyId: companyId || undefined,
      employeeId:
        isEmployeeSpecificReport(reportType)
          ? Number(getSelectedEmployeeId(reportType))
          : undefined,
      department:
        reportType === "no-withdrawal-by-period" && noWithdrawalDepartment
          ? noWithdrawalDepartment
          : undefined,
      format,
    };

    const response = await apiClient.post(
      format === "excel" ? "/exportAdditionalReportExcel" : "/exportAdditionalReportPdf",
      payload,
      { responseType: "blob" }
    );

    // O interceptor dos clients resolve a promise com o número 401 quando a
    // requisição falha, então a resposta precisa ser validada antes de virar Blob.
    const data = response?.data;
    if (!(data instanceof Blob)) {
      throw new Error(defaultReportError);
    }

    // Em caso de erro o servidor responde JSON; sem esta checagem o JSON era
    // entregue ao iframe como PDF e o navegador exibia
    // "Falha ao carregar documento PDF." em vez da mensagem real.
    const expectedContentType = format === "excel"
      ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      : "application/pdf";
    if (
      data.type &&
      !data.type.includes(expectedContentType) &&
      !data.type.includes("application/octet-stream")
    ) {
      throw new Error(await extractErrorMessage(data));
    }

    return new Blob([data], { type: expectedContentType });
  };

  const fetchReportPdf = (reportType: ReportType) =>
    fetchReportFile(reportType, "pdf");

  const handlePreview = async (card: ReportCard) => {
    try {
      setLoadingReport(card.type);
      const blob = await fetchReportPdf(card.type);
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl((currentUrl) => {
        if (currentUrl) {
          window.URL.revokeObjectURL(currentUrl);
        }
        return url;
      });
      setPreviewTitle(card.title);
    } catch (error) {
      console.error("Erro ao visualizar relatório adicional:", error);
      toast.error(error instanceof Error ? error.message : defaultReportError);
    } finally {
      setLoadingReport(null);
    }
  };

  const handleDownload = async (card: ReportCard) => {
    try {
      setLoadingReport(card.type);
      const blob = await fetchReportPdf(card.type);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const { startDate, endDate } = reportPeriods[card.type];
      // Sem o período no nome, gerar vários períodos seguidos sobrescreveria o mesmo arquivo.
      const periodSuffix = `_${startDate.replace(/-/g, "")}_a_${endDate.replace(/-/g, "")}`;
      link.download = `${card.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, "_")}${periodSuffix}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar relatório adicional:", error);
      toast.error(
        error instanceof Error ? error.message : "Erro ao baixar o PDF do relatório."
      );
    } finally {
      setLoadingReport(null);
    }
  };

  const handleDownloadExcel = async (card: ReportCard) => {
    if (!excelReportAllowed) {
      toast.error("A permissão para baixar relatórios em Excel não está habilitada.");
      return;
    }

    try {
      setLoadingReport(card.type);
      const blob = await fetchReportFile(card.type, "excel");
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const { startDate, endDate } = reportPeriods[card.type];
      const periodSuffix = `_${startDate.replace(/-/g, "")}_a_${endDate.replace(/-/g, "")}`;
      link.download = `${card.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, "_")}${periodSuffix}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar relatório em Excel:", error);
      toast.error(
        error instanceof Error ? error.message : "Erro ao baixar o relatório em Excel."
      );
    } finally {
      setLoadingReport(null);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewTitle("");
  };

  return (
    <LayoutSubmaster>
      <div className="report-config-page">
        <section className="dashboard-hero report-hero mb-4">
          <div className="report-hero__content">
            <span className="pill-badge">Relatório Adicional</span>
            <h1 className="dashboard-title">Relatórios adicionais</h1>
            <p className="dashboard-subtitle">
              Gere relatórios em PDF ou Excel usando os mesmos filtros e informações.
            </p>
          </div>
          <div className="report-hero__badge">
            <i className="bi bi-file-earmark-bar-graph" />
            <div>
              <span>Saída em PDF{excelReportAllowed ? " e Excel" : ""}</span>
              <strong>{reportCards.length} relatórios disponíveis</strong>
            </div>
          </div>
        </section>

        <div className="row g-4">
          {reportCards.map((card) => (
            <div key={card.type} className="col-lg-4">
              <section className="section-panel h-100 d-flex flex-column justify-content-between">
                <div>
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="metric-card__icon">
                      <i className={`bi ${card.icon} fs-4 text-primary`} />
                    </div>
                    <div>
                      <h3 className="mb-1" style={{ fontSize: "1.1rem" }}>
                        {card.title}
                      </h3>
                      <span className="pill-badge">
                        PDF{excelReportAllowed ? " / Excel" : ""}
                      </span>
                    </div>
                  </div>

                  <p className="text-secondary mb-0">{card.description}</p>

                  {reportCompanies.length > 0 && (
                    <div className="mt-3">
                      <label
                        className="form-label mb-1 d-block"
                        htmlFor={`${card.type}-company`}
                        style={periodLabelStyle}
                      >
                        Empresa
                      </label>
                      <select
                        id={`${card.type}-company`}
                        className="form-select"
                        value={reportCompanyIds[card.type]}
                        disabled={reportCompanies.length === 1 && !isCatalogReport(card.type)}
                        onChange={(event) =>
                          updateReportCompany(card.type, event.target.value)
                        }
                      >
                        {reportCompanies.length > 1 && !isCatalogReport(card.type) && (
                          <option value="">Selecione a empresa</option>
                        )}
                        <option value="all">Todas as empresas</option>
                        {reportCompanies.map((company) => (
                          <option value={company.value} key={company.value}>
                            {company.name}
                            {company.cnpj ? ` - ${formatCnpj(company.cnpj)}` : ""}
                            {company.isMain ? " (principal)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {isEmployeeSpecificReport(card.type) && (
                    <div className="mt-3">
                      <label
                        className="form-label mb-1 d-block"
                        htmlFor={`${card.type}-employee`}
                        style={periodLabelStyle}
                      >
                        Funcionário
                      </label>
                      <select
                        id={`${card.type}-employee`}
                        className="form-select"
                        value={getSelectedEmployeeId(card.type)}
                        disabled={
                          !reportEmployeesLoaded ||
                          !reportCompanyIds[card.type]
                        }
                        onChange={(event) =>
                          updateSelectedEmployeeId(card.type, event.target.value)
                        }
                      >
                        <option value="">Selecione o funcionário</option>
                        {(employeesByEmployeeReport[card.type] || []).map((employee) => (
                          <option value={employee.id} key={employee.id}>
                            {employee.name}
                          </option>
                        ))}
                      </select>
                      {reportEmployeesLoaded &&
                        reportCompanyIds[card.type] &&
                        (employeesByEmployeeReport[card.type] || []).length === 0 && (
                          <small className="text-secondary d-block mt-1">
                            Nenhum funcionário ativo encontrado para esta empresa.
                          </small>
                        )}
                    </div>
                  )}

                  {card.type === "no-withdrawal-by-period" && (
                    <div className="mt-3">
                      <label
                        className="form-label mb-1 d-block"
                        htmlFor={`${card.type}-department`}
                        style={periodLabelStyle}
                      >
                        Setor
                      </label>
                      <select
                        id={`${card.type}-department`}
                        className="form-select"
                        value={noWithdrawalDepartment}
                        disabled={
                          !reportEmployeesLoaded ||
                          !reportCompanyIds["no-withdrawal-by-period"]
                        }
                        onChange={(event) => setNoWithdrawalDepartment(event.target.value)}
                      >
                        <option value="">Todos os setores</option>
                        {noWithdrawalDepartments.map((department) => (
                          <option value={department} key={department}>
                            {department}
                          </option>
                        ))}
                      </select>
                      {reportEmployeesLoaded &&
                        reportCompanyIds["no-withdrawal-by-period"] &&
                        noWithdrawalDepartments.length === 0 && (
                          <small className="text-secondary d-block mt-1">
                            Nenhum setor cadastrado para os funcionários desta empresa.
                          </small>
                        )}
                    </div>
                  )}

                  <div className="row g-2 mt-3">
                      <div className="col-6">
                        <label
                          className="form-label mb-1 d-block"
                          htmlFor={`${card.type}-startDate`}
                          style={periodLabelStyle}
                        >
                          Data início
                        </label>
                        <input
                          id={`${card.type}-startDate`}
                          type="date"
                          className="form-control"
                          value={reportPeriods[card.type].startDate}
                          max={reportPeriods[card.type].endDate || undefined}
                          onChange={(event) =>
                            updateReportPeriod(card.type, "startDate", event.target.value)
                          }
                        />
                      </div>
                      <div className="col-6">
                        <label
                          className="form-label mb-1 d-block"
                          htmlFor={`${card.type}-endDate`}
                          style={periodLabelStyle}
                        >
                          Data fim
                        </label>
                        <input
                          id={`${card.type}-endDate`}
                          type="date"
                          className="form-control"
                          value={reportPeriods[card.type].endDate}
                          min={reportPeriods[card.type].startDate || undefined}
                          onChange={(event) =>
                            updateReportPeriod(card.type, "endDate", event.target.value)
                          }
                        />
                      </div>
                  </div>
                </div>

                <div className="d-flex gap-2 mt-4">
                  <button
                    type="button"
                    className="btn btn-outline-primary flex-fill"
                    onClick={() => handlePreview(card)}
                    disabled={
                      !reportCompaniesLoaded ||
                      loadingReport === card.type ||
                      (isEmployeeSpecificReport(card.type) && !reportEmployeesLoaded)
                    }
                  >
                    {loadingReport === card.type ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                    ) : (
                      <i className="bi bi-eye me-2" />
                    )}
                    Visualizar PDF
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary flex-fill"
                    onClick={() => handleDownload(card)}
                    disabled={
                      !reportCompaniesLoaded ||
                      loadingReport === card.type ||
                      (isEmployeeSpecificReport(card.type) && !reportEmployeesLoaded)
                    }
                  >
                    {loadingReport === card.type ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                    ) : (
                      <i className="bi bi-download me-2" />
                    )}
                    Baixar PDF
                  </button>
                </div>
                {excelReportAllowed && (
                  <button
                    type="button"
                    className="btn btn-success w-100 mt-2"
                    onClick={() => handleDownloadExcel(card)}
                    disabled={
                      !reportCompaniesLoaded ||
                      loadingReport === card.type ||
                      (isEmployeeSpecificReport(card.type) && !reportEmployeesLoaded)
                    }
                  >
                    {loadingReport === card.type ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                    ) : (
                      <i className="bi bi-file-earmark-excel me-2" />
                    )}
                    Baixar Excel
                  </button>
                )}
              </section>
            </div>
          ))}

        </div>
      </div>

      {previewUrl && (
        <>
          <div className="modal-backdrop fade show" onClick={closePreview} />
          <div className="modal fade show d-block" tabIndex={-1} aria-hidden="true">
            <div className="modal-dialog modal-xl modal-dialog-centered">
              <div className="modal-content">
                <div
                  className="modal-header"
                  style={{ background: "linear-gradient(135deg, #0d6efd, #4f8cff)" }}
                >
                  <div>
                    <h5 className="modal-title text-white mb-1">
                      <i className="bi bi-filetype-pdf me-2" />
                      {previewTitle}
                    </h5>
                    <small className="text-white-50">
                      Pré-visualização do PDF gerado com os ícones da empresa e do Track EPI.
                    </small>
                  </div>
                  <button
                    type="button"
                    className="mt-4 mr-4 btn-close btn-close-white"
                    onClick={closePreview}
                  />
                </div>
                <div className="modal-body p-0 report-preview-modal__body">
                  <iframe
                    src={previewUrl}
                    title={previewTitle}
                    className="w-100 h-100 border-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </LayoutSubmaster>
  );
}

export default ConfigAdditionalReportPage;
